// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { DSNSnapshot, Dish } from '../types';
import { getSpacecraftInfo, CATEGORY_COLOR } from '../data/spacecraft';
import { BAND_COLOR } from '../utils/bands';
import {
  compassBearing,
  formatAzEl,
  formatDataRate,
  formatRTLT,
  formatClockUTC,
  formatNumber,
} from '../utils/format';
import { extractPairs } from '../state';

function renderCompass(dish: Dish): string {
  const az = dish.azimuthAngle ?? 0;
  const el = dish.elevationAngle ?? 0;
  const active = dish.activity.toLowerCase().includes('spacecraft');
  const color = active ? 'var(--accent-primary)' : 'var(--text-muted)';
  const rad = (az - 90) * (Math.PI / 180);
  const tipR = 22 + ((el / 90) * 6);
  const tipX = 32 + Math.cos(rad) * tipR;
  const tipY = 32 + Math.sin(rad) * tipR;
  const pointingTip = `${dish.name} pointing — Az/El ${formatAzEl(dish.azimuthAngle, dish.elevationAngle)}${dish.azimuthAngle !== null ? ` (${compassBearing(dish.azimuthAngle)})` : ''}`;
  return `
    <svg class="dish-compass" viewBox="0 0 64 64" aria-hidden="true" data-tip="${pointingTip}">
      <circle cx="32" cy="32" r="28" stroke="var(--border-default)" stroke-width="1" fill="none"/>
      <circle cx="32" cy="32" r="18" stroke="var(--border-subtle)" stroke-width="1" fill="none"/>
      <line x1="32" y1="6" x2="32" y2="10" stroke="var(--text-tertiary)" stroke-width="1"/>
      <line x1="32" y1="54" x2="32" y2="58" stroke="var(--text-tertiary)" stroke-width="1"/>
      <line x1="6" y1="32" x2="10" y2="32" stroke="var(--text-tertiary)" stroke-width="1"/>
      <line x1="54" y1="32" x2="58" y2="32" stroke="var(--text-tertiary)" stroke-width="1"/>
      <text x="32" y="8" font-size="7" text-anchor="middle" fill="var(--text-tertiary)">N</text>
      <line x1="32" y1="32" x2="${tipX.toFixed(2)}" y2="${tipY.toFixed(2)}" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
      <circle cx="${tipX.toFixed(2)}" cy="${tipY.toFixed(2)}" r="2.5" fill="${color}"/>
      <circle cx="32" cy="32" r="2" fill="${color}"/>
    </svg>
  `;
}

function renderDishCard(dish: Dish): string {
  const isIdle = !dish.activity.toLowerCase().includes('spacecraft');
  const targetChips = dish.targets
    .filter((t) => t.name && t.name !== '')
    .map((target) => {
      const info = getSpacecraftInfo(target.name);
      const color = CATEGORY_COLOR[info.category];
      const chipTip = `${info.name} — ${info.mission}${target.rtlt > 0 ? ` · round-trip ${formatRTLT(target.rtlt * 2)}` : ''} · click for details`;
      return `
        <button class="target-chip" data-spacecraft="${target.name}" data-tip="${chipTip}" aria-label="${chipTip}">
          <span class="dot" style="background: ${color}"></span>
          <span>${info.name}</span>
          ${target.rtlt > 0 ? `<span style="color: var(--text-tertiary); font-family: var(--font-mono); font-size: 10px;">${formatRTLT(target.rtlt * 2)}</span>` : ''}
        </button>
      `;
    })
    .join('');

  const signalTags = dish.signals
    .filter((s) => s.band !== '' || s.active)
    .map((s) => {
      const cls = s.active ? 'band-tag active' : 'band-tag';
      const arrow = s.direction === 'up' ? '↑' : '↓';
      const color = BAND_COLOR[s.band] ?? '#7f8c8d';
      const rate = s.dataRate > 0 ? ` · ${formatDataRate(s.dataRate)}` : '';
      const sigTip = `${s.direction === 'up' ? 'Uplink' : 'Downlink'} · ${s.band ? `${s.band}-band` : 'band unknown'}${s.dataRate > 0 ? ` · ${formatDataRate(s.dataRate)}` : ''} · ${s.active ? 'signal active' : 'inactive'}`;
      return `<span class="${cls}" style="color: ${s.active ? color : 'var(--text-tertiary)'}" data-tip="${sigTip}" aria-label="${sigTip}">${arrow} ${s.band || '—'}${rate}</span>`;
    })
    .join('');

  const mspaBadge = dish.isMSPA ? ' · <span class="glossary-link" data-term="mspa">MSPA</span>' : '';
  const ddorBadge = dish.isDDOR ? ' · <span class="glossary-link" data-term="ddor">DDOR</span>' : '';
  const wind = dish.windSpeed !== null ? `${dish.windSpeed} km/h` : '—';

  return `
    <div class="dish-card ${isIdle ? 'idle' : ''}">
      ${renderCompass(dish)}
      <div class="dish-info">
        <div class="dish-name">${dish.name}</div>
        <div class="dish-activity">${dish.activity}${mspaBadge}${ddorBadge}</div>
        ${targetChips ? `<div class="dish-targets">${targetChips}</div>` : ''}
        ${signalTags ? `<div class="dish-signals">${signalTags}</div>` : ''}
        <div class="dish-meta-row">
          <span><strong>Az/El:</strong> ${formatAzEl(dish.azimuthAngle, dish.elevationAngle)} ${dish.azimuthAngle !== null ? `(${compassBearing(dish.azimuthAngle)})` : ''}</span>
          <span><strong>Wind:</strong> ${wind}</span>
        </div>
      </div>
    </div>
  `;
}

export function renderNow(snapshot: DSNSnapshot): string {
  const pairs = extractPairs(snapshot);
  const activeDishes = snapshot.complexes.flatMap((c) => c.dishes).filter((d) => d.activity.toLowerCase().includes('spacecraft')).length;
  const totalDishes = snapshot.complexes.flatMap((c) => c.dishes).length;
  const uniqSc = new Set(pairs.filter((p) => p.spacecraft && p.spacecraft !== 'DSN' && p.spacecraft !== 'DSS').map((p) => p.spacecraft));

  const complexCards = snapshot.complexes
    .map((complex) => {
      const localTime = new Date(complex.timeUTC + complex.timeZoneOffset);
      const offsetH = complex.timeZoneOffset / 3_600_000;
      const offsetLabel = offsetH >= 0 ? `UTC+${offsetH}` : `UTC${offsetH}`;
      return `
        <div class="complex-card">
          <div class="complex-head">
            <div>
              <div class="complex-name">${complex.friendlyName}</div>
              <div class="complex-sub">${complex.country} · ${complex.code.toUpperCase()}</div>
            </div>
            <div class="complex-local">
              ${formatClockUTC(localTime).replace(' UTC', '')}
              <small>${offsetLabel}</small>
            </div>
          </div>
          <div class="dish-list">
            ${complex.dishes.map(renderDishCard).join('')}
          </div>
        </div>
      `;
    })
    .join('');

  return `
    <div class="view-header">
      <h2 class="view-title">Live network status</h2>
      <p class="view-subtitle">
        ${formatNumber(activeDishes)} of ${formatNumber(totalDishes)} antennas actively tracking spacecraft — listening to
        ${formatNumber(uniqSc.size)} missions across the three
        <span class="glossary-link" data-term="complex">complexes</span>.
        Feed refreshes every 10 seconds. Click any spacecraft chip for mission details.
      </p>
    </div>
    <div class="complex-grid">
      ${complexCards}
    </div>
  `;
}
